"use client";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import Recommandations from "../components/Recommandations.jsx";
import Script from "next/script";
import { faMagnifyingGlass, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import BtnMeContacter from "../components/BtnMeContacter.js";
import BlogSection from "../components/BlogSection.jsx";
import { useAllArticles } from "../lib/hooks/allArticlesCosmic.js";

export default function HomeProfil({ jsonLdReviews }) {
  const [isClient, setIsClient] = useState(false);
  const { articles } = useAllArticles(4);

  useEffect(() => {
    setIsClient(true);
  }, []);
  return (
    <>
      {/* Script JSON-LD avis injecté côté serveur (vérifiez dans le HTML source) */}
      {jsonLdReviews && (
        <Script
          id="jsonld-reviews"
          type="application/ld+json"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLdReviews),
          }}
        />
      )}
      <main className="flex flex-col gap-5">
        {/* HERO */}
        <div className="relative h-[200px] w-full overflow-hidden bg-[url('/ordi-cafe-fond.webp')] bg-cover bg-no-repeat bg-top"></div>
        <section className="min-h-[80vh] flex flex-col justify-center px-10 max-w-6xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Développeur web freelance
            <br />
            création de sites & performance web
          </h1>

          <p className="text-lg text-gray-600 max-w-2xl mb-12">
            15 ans d’expérience en développement web. J’accompagne les
            entreprises, indépendants et recruteurs sur des projets fiables,
            performants et bien référencés.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {/* CLIENTS */}
            <div className="border rounded-xl p-6 hover:shadow-lg transition">
              <h2 className="text-xl font-semibold mb-3">
                Vous avez besoin d’un site web ?
              </h2>
              <p className="text-gray-600 mb-6">
                Vous êtes un artisan, un particulier ou une startup ? Je crée,
                refonds, maintiens et optimise votre site web ainsi que son
                référencement naturel pour développer votre visibilité en ligne.
              </p>
              <motion.a
                whileHover="hover"
                href="/services"
                className="mt-5 inline-flex items-center px-4 py-2 bg-bleuciel text-white rounded-full hover:bg-blue transition duration-300 mx-2"
                aria-label="Voir les services web"
                title="Services Web">
                <motion.div
                  className="mr-2"
                  variants={{
                    rest: { rotate: 0 },
                    hover: {
                      rotate: 360,
                      transition: { duration: 0.6 },
                    },
                  }}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </motion.div>
                Créer ou améliorer mon site
              </motion.a>
            </div>

            {/* RECRUTEURS */}
            <div className="border rounded-xl p-6 hover:shadow-lg transition">
              <h2 className="text-xl font-semibold mb-3">
                Vous êtes recruteur ou responsable technique ?
              </h2>
              <p className="text-gray-600 mb-6">
                Développeur web freelance, je suis disponible pour des missions
                front-end, back-end ou fullstack, orienté javascript.
              </p>

              <motion.a
                whileHover="hover"
                href="/profil-technique"
                className="mt-5 inline-flex items-center px-4 py-2 bg-neutral text-white rounded-full hover:bg-blue transition duration-300 mx-2"
                aria-label="Profil développeur web"
                title="Profil développeur Fullstack">
                <motion.div
                  className="mr-2"
                  variants={{
                    rest: { rotate: 0 },
                    hover: {
                      rotate: 360,
                      transition: { duration: 0.6 },
                    },
                  }}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </motion.div>
                Voir mon profil développeur
              </motion.a>
            </div>
          </div>
        </section>

        {/* PREUVES */}
        <section className="bg-gray-50 py-10 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold">15</p>
              <p className="text-gray-600">ans d’expérience</p>
            </div>
            <div>
              <p className="text-3xl font-bold">Web</p>
              <p className="text-gray-600">front & back-end</p>
            </div>
            <div>
              <p className="text-3xl font-bold">SEO</p>
              <p className="text-gray-600">optimisation du référencement</p>
            </div>
            <div>
              <p className="text-3xl font-bold">France</p>
              <p className="text-gray-600">freelance Marseille</p>
            </div>
          </div>
        </section>

        {/* PARCOURS CLIENTS */}
        <section className="px-6 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-10">
            Pour les entreprises & indépendants
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <ServiceCard
              title="Création & refonte de site"
              text="Sites vitrines modernes, rapides et optimisés pour Google."
              ImgAlt="Création & refonte de site internet - Applications modernes"
              ImgSrc="/creation-refonte-site-web.png"
            />
            <ServiceCard
              title="Maintenance & sécurité"
              text="Suivi technique, mises à jour et sérénité."
              ImgAlt="Maintenance et sécurité des sites web"
              ImgSrc="/maintenance-web.jpg"
            />
            <ServiceCard
              title="Référencement naturel (SEO)"
              text="Améliorer votre visibilité sur le long terme."
              ImgAlt="Référencement naturel SEO"
              ImgSrc="/referencement-progression.jpg"
            />
          </div>
          <motion.a
            whileHover="hover"
            href="/services"
            className="inline-flex items-center px-4 py-2 mt-4 bg-bleuciel text-white rounded-full hover:bg-blue transition duration-300 mx-2"
            aria-label="Services Web"
            title="Services Web">
            <motion.div
              className="mr-2"
              variants={{
                rest: { rotate: 0 },
                hover: {
                  rotate: 360,
                  transition: { duration: 0.6 },
                },
              }}>
              <FontAwesomeIcon icon={faSearch} />
            </motion.div>
            En savoir plus
          </motion.a>
        </section>

        {/* PARCOURS RECRUTEURS */}
        <section className="bg-gray-50 py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-10">
              Pour les spécialistes du web
            </h2>

            <ul className="grid md:grid-cols-2 gap-6 text-gray-700">
              <li>✔ Front-end : Next.js, React, NuxtJs, Vue.js</li>
              <li>
                ✔ Back-end : NodeJs, PHP, API, bases de données (MongoDB,
                PostgreSQL)
              </li>
              <li>✔ Code maintenable, performance, SEO technique</li>
              <li>✔ Freelance autonome, intégration rapide</li>
            </ul>

            <motion.a
              whileHover="hover"
              href="/profil-technique"
              className="mt-5 inline-flex items-center px-4 py-2 bg-neutral text-white rounded-full hover:bg-blue transition duration-300 mx-2"
              aria-label="Profil développeur web"
              title="Profil développeur Fullstack">
              <motion.div
                className="mr-2"
                variants={{
                  rest: { rotate: 0 },
                  hover: {
                    rotate: 360,
                    transition: { duration: 0.6 },
                  },
                }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </motion.div>
              Mon profil développeur
            </motion.a>
            <motion.a
              whileHover="hover"
              href="/projets"
              className="mt-5 inline-flex items-center px-4 py-2 bg-neutral text-white rounded-full hover:bg-blue transition duration-300 mx-2"
              aria-label="Voir les projets techniques"
              title="Références Web">
              <motion.div
                className="mr-2"
                variants={{
                  rest: { rotate: 0 },
                  hover: {
                    rotate: 360,
                    transition: { duration: 0.6 },
                  },
                }}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </motion.div>
              Mes projets techniques
            </motion.a>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-10 px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Parlons de votre projet</h2>
          <p className="text-gray-600 mb-8">
            Site web, référencement ou mission freelance.
          </p>
          <BtnMeContacter />
        </section>

        {/* Section des recommandations clients */}
        <Recommandations />
        <BlogSection isClient={isClient} articles={articles} />
      </main>
    </>
  );
}

function ServiceCard({ title, text, ImgSrc, ImgAlt }) {
  return (
    <div className="border rounded-xl p-6">
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <div className=" mt-3">
        <img
          src={ImgSrc}
          alt={ImgAlt}
          className="mb-4 rounded-md max-h-24"
          width={150}
          height={150}
        />
      </div>
      <p className="text-gray-600">{text}</p>
    </div>
  );
}

ServiceCard.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  ImgSrc: PropTypes.string.isRequired,
  ImgAlt: PropTypes.string.isRequired,
};

HomeProfil.propTypes = {
  jsonLdReviews: PropTypes.any,
};
